import { AllieSelectOptionProps } from '../../../components';
import { EfficiencyConfirmingProps, EfficiencyWizardEndedProps } from '../../components';
import { StopTypes } from './AssignReasons.enum';

export const NOT_APPLICABLE_FAILURE_MODE = -1;

type MotiveOption = AllieSelectOptionProps & {
  failureModes?: FailureMode[];
};

type GroupsMotive = AllieSelectOptionProps & {
  motives: MotiveOption[];
  isMachineRelated: boolean;
};
type Components = AllieSelectOptionProps & { components: AllieSelectOptionProps[] };

interface TrackingEvents {
  step2DropdownTrackingEvent: (field: string, isInputSearch: boolean) => void;
  failureModeSelected?: (data: {
    failureModeId: number;
    stopReasonId: number;
    machineId?: number;
    isRestrictedMode: boolean;
    totalOptionsShown: number;
  }) => void;
  lastReasonsModal: (
    trackingEvent: string,
    selectedReason?: string | null,
    failureModeId?: number | null,
    failureModeName?: string | null
  ) => void;
}

export interface Step1Props {
  labels: {
    title: string;
    typeStop: string;
    planned: string;
    unplanned: string;
  };
}

export interface Step1DataProps {
  typeStop: string | undefined;
}

export type FailureModeOption = AllieSelectOptionProps & {
  machineIds?: number[];
  isRestricted?: boolean;
  preserveForEdit?: boolean;
};

export interface Step2Props {
  labels: {
    title: string;
    groupMotive: string;
    motive: string;
    machine: string;
    component: string;
    empty: string;
    note: string;
    failureMode: string;
    notApplicable: string;
    optional?: string;
  };
  groupsMotive?: Record<string, GroupsMotive[]>;
  machines?: Components[];
  failureModesByMotive?: Record<number, FailureModeOption[]>;
  failureModesLoading?: boolean;
  preserveNoMachineSelection?: boolean;
  trackingEvents?: Partial<Omit<TrackingEvents, 'lastReasonsModal'>>;
}

export interface Step2DataProps {
  groupMotive?: number;
  motive?: number;
  machine?: number;
  component?: number;
  isMachineRelated?: boolean;
  failureMode?: number;
  note?: string;
  resolvedStopType?: string;
}

export interface FailureMode {
  id: number;
  stopReasonId: number;
  name: string;
  isActive: boolean;
  machineIds?: number[];
  isRestricted?: boolean;
}

export interface LastReasonsAssign {
  stopReasonId: number;
  categoryId: number | null;
  categoryValue: string | null;
  subCategoryId: number;
  reasonValue: string;
  stopType: 'planned' | 'unplanned';
  isActive: boolean;
  lastAssignedMachineId: number | null;
  lastAssignedMachineValue: string | null;
  lastAssignedComponentId: number | null;
  failureModes?: FailureMode[];
}

export interface DataAssignReasonForm {
  id?: number;
  timeRange?: [number, number];
  originalTimeRange?: [number, number];
  stopType?: string;
  stopReasonId?: number;
  machineId?: number | null;
  componentId?: number;
  note?: string;
  failureModeId?: number | null;
}

interface HandlersAssignReasonForm {
  onCloseModal: () => void;
  isOpen: boolean;
  setIsOpen: (a: boolean) => void;
}

export interface RenderAssignReasonFormProps extends HandlersAssignReasonForm {
  data: DataAssignReasonForm;
  isEdit?: boolean;
  lastReasonsAssign?: LastReasonsAssign[];
}

export interface RequiredFieldsConfig {
  machineRequired: boolean;
  componentRequired: boolean;
  failureModeRequired: boolean;
  commentRequired: boolean;
  groupFirstEnabled: boolean;
}

export interface AssignReasonFormProps extends HandlersAssignReasonForm {
  className?: string;
  editSource?: string;
  labels: {
    nextButton: string;
    prevButton: string;
    cancelButton: string;
    closeButton: string;
    confirmButton: string;
    title: string;
    subtitle: string;
    stopTypeLabels: {
      all: string;
      planned: string;
      unplanned: string;
    };
    step0: {
      title: string;
      subtitle: string;
      emptyStateText: string;
      emptyStateCTA: string;
      selectReason: string;
    };
    step1: {
      title: string;
      subtitle: string;
    };
    step2: {
      title: string;
      subtitle: string;
      stopTypeFilterSubtitle: string;
    };
    quickConfirmationStep: {
      title: string;
      subtitle: string;
      saveButton: string;
      labelTypeStop: string;
      labelMachine: string;
      labelMotive: string;
      labelGroup: string;
      labelFailureMode: string;
      labelNotApplicable: string;
    };
    quickConfirmModalTitle: string;
    chooseAnotherMethodButton: string;
    note: string;
    notePlaceholder: string;
  };
  requiredFieldsConfig?: RequiredFieldsConfig;
  step1Props: Step1Props;
  step2Props: Step2Props;
  confirmingLoadingProps: EfficiencyConfirmingProps;
  wizardEndedProps: EfficiencyWizardEndedProps;
  data: DataAssignReasonForm;
  onConfirm: (a: any) => Promise<void>;
  onError?: () => void;
  setMachineSelected?: React.Dispatch<React.SetStateAction<number | undefined>>;
  onClickOpenModalReason: (cb: () => void, isResized: boolean) => void;
  isEdit?: boolean;
  lastReasonsAssign?: LastReasonsAssign[];
  trackingEvents?: Omit<TrackingEvents, 'step2DropdownTrackingEvent'>;
  loading?: boolean;
  stopTypeSelectionHandling?: (stopType: StopTypes) => void;
  handleCategorySelection?: (value?: number, stopType?: StopTypes) => void;
}
